<template>
	<ListView
		:items="items"
		:empty-msg="emptyMsg"
		:swipable="swipable"
		:swipe-left-options="swipeLeftOptions"
		:swipe-right-options="swipeRightOptions"
		:list-container-class="listContainerClass"
		:list-body-class="['tbody']"
		:recycler-page-size="recyclerPageSize"
		:enable-recycler-view="enableRecyclerView"
	>
		<template #no-results>
			<slot name="no-results" />
		</template>
		<template #list-header>
			<div
				v-if="headerVisible"
				class="thead"
				:class="{ 'is-highlight-tool': isHighlightTool }"
			>
				<div class="tr scrollbar-padding">
					<div
						v-if="canSelect"
						class="th checkbox-wrapper -col-checkbox"
						:class="{ 'some-options-selected': hasSomeSelected }"
					>
						<Checkbox
							id="custom-table-all"
							v-model="selectAll"
							:theme="`dark`"
							:size="`small`"
							:neutral="hasSomeSelected"
						/>
					</div>
					<div
						v-else
						class="th spacer"
					/>
					<div
						v-for="col in columns"
						:key="`col-${col.key}`"
						class="th"
						:class="{
							'text-right': col.key === 'time' || col.key === 'duration',
						}"
						:style="`width: ${col.size}%`"
					>
						{{ col.label }}
					</div>
				</div>
			</div>
		</template>

		<template #item-row="{ item }">
			<slot
				name="item-row"
				:entry="item"
			>
				<TagRow
					:entry="item"
					:checked="isSelected(item)"
					:is-hovered="hoveredItemId === item.id"
					:sport-name="sportName"
					:columns="columns"
					:can-select="canSelect"
					:checkbox-always-visible="checkboxAlwaysVisible"
					:menu-items="item.contextMenuEnabled === true ? rowContextMenuItems : null"
					:is-highlight-tool="isHighlightTool"
					@row-click="handleRowClick"
					@row-hover="handleRowHover"
					@row-hover-end="handleRowHoverEnd"
					@selection-changed="handleRowSelectionChange"
				>
					<!-- action cell -->
					<template #col-action="{ entry, col }">
						<div
							v-if="isHighlightTool"
							class="tag-name -highlight-tool"
						>
							<div class="tag-name__action">
								<Icon
									v-if="currentlyPreviewingTag && currentlyPreviewingTag.id === entry.id"
									icon-name="button-stop-filled"
									size="sm"
									class="preview-stop-icon"
									@click.stop="$emit('stop-preview')"
								/>
								<Icon
									v-else
									icon-name="button-play-filled"
									size="sm"
									class="preview-play-icon"
									@click.stop="$emit('preview', entry)"
								/>
							</div>
							<div class="tag-name__label">
								{{ transTagLabel(entry[col.key], sportName, entry.tag ? entry.tag.tag.type : null) }}
							</div>
							<LoudnessIndicator
								v-if="entry.audioScore > 0"
								:value="entry.audioScore"
							/>
						</div>
						<LabelWithTooltip
							v-else
							v-slot="{ label }"
							:label="transTagLabel(entry[col.key], sportName, entry.tag ? entry.tag.tag.type : null)"
						>
							<div class="tag-name">
								{{ label }}
							</div>
						</LabelWithTooltip>
					</template>

					<!-- participant name cell -->
					<template #col-participantName="{ entry, col }">
						<LabelWithTooltip
							v-if="entry[col.key]"
							v-slot="{ label }"
							:label="entry[col.key]"
							:overflow-length="10"
						>
							<div class="athlete-name">
								{{ label }}
							</div>
						</LabelWithTooltip>
					</template>

					<!-- time -->
					<template #col-time="{ entry }">
						<span
							v-tooltip="entry.tooltip"
							class="timestamp"
							:class="{ '-greyed-out': entry.isOutsideVideo }"
						>
							{{ entry.time }}
						</span>
						<span
							v-if="entry.gameMinute"
							class="game-minute"
						>({{ entry.gameMinute }}’)</span>
					</template>

					<template #col-ratings="{ entry }">
						<RatingView :rating="entry.rating" />
					</template>

					<template #col-highlight="{ entry }">
						<Icon
							v-if="highlightIqSelectedItems.find(item => item.id === entry.id)"
							icon-name="highlight-video"
							size="sm"
							color-class="mr-1"
						/>
					</template>

					<template #row-actions="{ menuItems, entry }">
						<TagRowInlineMenu
							:menu-items="menuItems"
							:entry="entry"
						/>
					</template>
				</TagRow>
			</slot>
		</template>
	</ListView>
</template>
<script>
import Vue from 'vue';
import ListView from '@components/lists/ListView';
import Checkbox from '@components/checkbox/Checkbox';
import Icon from '@components/Icon';
import TagRow from './TagRow';
import { mapGetters } from 'vuex';
import TagRowInlineMenu from '@videotools/js/one/components/TagList/TagRowInlineMenu';
import RatingView from '@videotools/js/one/components/Rating/RatingView';
import LoudnessIndicator from './LoudnessIndicator';

const EVENTS = {
	DELETE:            'delete',
	SELECTION_CHANGED: 'selection-changed',
	ROW_CLICK:         'row-click',
	ROW_HOVER:         'row-hover',
	ROW_HOVER_END:     'row-hover-end',
	TAP:               'tap',
	LONG_TAP:          'long-tap',
	EDIT:              'edit',
	SHARE:             'share',
};

const DIRECTION = {
	ASC:  'asc',
	DESC: 'desc',
};

export default {
	name:       'TagGrid',
	components: { TagRowInlineMenu, TagRow, Icon, Checkbox, ListView, RatingView, LoudnessIndicator },
	props:      {
		items: {
			type:    Array,
			default: () => [],
		},
		selectedItems: {
			type:    Array,
			default: () => [],
		},
		highlightIqSelectedItems: {
			type:    Array,
			default: () => [],
		},
		isHighlightTool: {
			type:    Boolean,
			default: false,
		},
		sportName: {
			type:     String,
			required: true,
		},
		canSelect: {
			type:    Boolean,
			default: true,
		},
		swipable: {
			type:    Boolean,
			default: true,
		},
		swipeLeftOptions: {
			type:    Object,
			default: null,
		},
		swipeRightOptions: {
			type:    Object,
			default: null,
		},
		tagIconVisible: {
			type:    Boolean,
			default: true,
		},
		checkboxAlwaysVisible: {
			type:    Boolean,
			default: false,
		},
		showInlineOptions: {
			type:    Boolean,
			default: false,
		},
		emptyMsg: {
			type:    String,
			default: null,
		},
		rowContextMenuItems: {
			type:    Array,
			default: null,
		},
		showParticipant: {
			type:    Boolean,
			default: true,
		},
		headerVisible: {
			type:    Boolean,
			default: true,
		},
		recyclerPageSize: {
			type:    Number,
			default: null,
		},
		enableRecyclerView: {
			type:    Boolean,
			default: false,
		},
		currentlyPreviewingTag: {
			type:    Object,
			default: null,
		},
		hoveredItemId: {
			type:    Number,
			default: null,
		},
	},
	data() {
		return {
			currentSortColumn:    null,
			currentSortDirection: 'asc',
			selectionMap:         {},
		};
	},
	computed: {
		...mapGetters({
			isMobileWebApp:      'app/isMobileWebApp',
			isPlayerCompetition: 'event/isPlayerCompetition',
		}),
		columns() {
			const columns = [
				{
					key:   'action',
					label: this.actionLabel,
					size:  this.getColumnSize('action'),
				},
				...(this.showParticipant
					? [
						{
							key:   'participantName',
							label: this.teamOrAthleteLabel,
							size:  this.getColumnSize('team/athlete'),
						},
					  ]
					: []),
				{
					key:   'time',
					label: this.timeLabel,
					size:  this.getColumnSize('time'),
				},
			];

			if (!this.isMobileWebApp) {
				columns.push({
					key:     'duration',
					label:   this.durationLabel,
					size:    this.getColumnSize('duration'),
					visible: !this.isMobileWebApp,
				});
			}

			if (this.isHighlightTool) {
				columns.push({
					key:     'highlight',
					label:   '',
					size:    10,
					visible: !this.isMobileWebApp,
				});
			}

			if (!this.isMobileWebApp && this.hasRatings) {
				columns.push({
					key:     'ratings',
					label:   '',
					size:    this.getColumnSize('ratings'),
					visible: !this.isMobileWebApp,
				});
			}

			if (this.showInlineOptions) {
				columns.push({
					key:   'options',
					label: '',
				});
			}

			return columns;
		},
		listContainerClass() {
			return ['table', 'table--responsive', 'table--no-border', 'is-dark', 'is-sticky', ...(this.isMobileWebApp ? ['is-mobile'] : [])];
		},
		teamOrAthleteLabel() {
			return this.$I18n
				? (!this.isPlayerCompetition
					? this.$I18n.trans_choice('commons.team', 1)
					: this.$I18n.trans_choice('commons.athlete', 1))
				: 'Team';
		},
		selectAll: {
			get() {
				const values = Object.values(this.selectionMap);
				return values.length > 0 && values.every(Boolean);
			},
			set(value) {
				if (value) {
					this.initSelectionMap(true);
				} else {
					this.initSelectionMap(false);
				}
				this.emitChange();
			},
		},
		isAscending() {
			return this.currentSortDirection === DIRECTION.ASC;
		},
		isDescending() {
			return this.currentSortDirection === DIRECTION.DESC;
		},
		selected() {
			const items = [];

			for (const [itemId, selected] of Object.entries(this.selectionMap)) {
				if (selected) {
					const item = this.items.find((it) => it.id == itemId);
					if (item) {
						items.push(item);
					}
				}
			}

			return items;
		},
		selectionSize() {
			return Object.values(this.selectionMap).filter(Boolean).length;
		},
		hasSomeSelected() {
			return this.selectionSize > 0 && this.selectionSize < this.items.length;
		},
		hasRatings() {
			return this.items.filter(tag => tag.rating > 0).length > 0;
		},
	},
	watch: {
		selectedItems() {
			this.syncSelectionMap();
		},
	},
	mounted() {
		this.syncSelectionMap();
	},
	methods: {
		initSelectionMap(selected = false) {
			this.selectionMap = Object.fromEntries(this.items.map((item) => [item.id, selected]));
		},
		syncSelectionMap() {
			this.initSelectionMap();
			for (const item of this.selectedItems) {
				if (!this.selectionMap[item.id]) {
					Vue.set(this.selectionMap, item.id, true);
				}
			}
		},
		getColumnSize(columnKey) {
			switch (columnKey) {
			case 'action': {
				return !this.isMobileWebApp ? 40 : 50;
			}

			case 'team/athlete': {
				return !this.isMobileWebApp ? (this.showInlineOptions ? 13 : 20) : 25;
			}

			case 'time': {
				return !this.isMobileWebApp ? (this.showInlineOptions ? 12 : 22) : 25;
			}

			case 'duration': {
				return this.showInlineOptions ? 10 : 21;
			}

			case 'ratings': {
				return 14;
			}
			}
		},
		isSelected(tag) {
			return Boolean(this.selectionMap[tag.id]);
		},
		handleSortBy(column) {
			if (column === this.currentSortColumn) {
				this.currentSortDirection = this.currentSortDirection === DIRECTION.ASC ? DIRECTION.DESC : DIRECTION.ASC;
			}
			this.currentSortColumn = column;
		},
		handleRowSelectionChange({ entry: item, selected }) {
			Vue.set(this.selectionMap, item.id, selected);
			this.emitChange();
		},
		handleRowClick(item) {
			this.$emit(EVENTS.ROW_CLICK, item);
		},
		handleRowHover(item) {
			this.$emit(EVENTS.ROW_HOVER, item);
		},
		handleRowHoverEnd() {
			this.$emit(EVENTS.ROW_HOVER_END);
		},
		emitChange() {
			this.$emit(EVENTS.SELECTION_CHANGED, this.selected);
		},
	},
};
</script>

<style lang="scss" scoped>
@import "@shared/sass/shared-variables";

.game-minute {
	color: var(--color-grey-300);
}

.timestamp.-greyed-out {
	opacity: 0.5;
}

.th {
	&.-col-checkbox {
		.thead.is-highlight-tool & {
			order: 5;
			padding: 0;
		}
	}
}

.tag-name {
	display: flex;
	align-items: center;
	gap: $sp3;

	&.-highlight-tool {
		overflow: hidden;
	}
}

.tag-name__action {
	display: none;

	@media (hover: hover) {
		.tr:hover & {
			display: inline-flex;
		}
	}

	@media (hover: none) {
		display: inline-flex;
	}
}

.tag-name__label {
	display: inline-block;
	text-overflow: ellipsis;
	overflow: hidden;
	white-space: nowrap;
}

.preview-play-icon {
	cursor: pointer;

	&:hover {
		color: var(--color-grey-300);
	}
}

.preview-stop-icon {
	cursor: pointer;

	&:hover {
		color: var(--color-grey-300);
	}
}

</style>
